(() => {
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 200;

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightText = (text, term) => {
    const fragment = document.createDocumentFragment();
    if (!term) {
      fragment.append(document.createTextNode(text));
      return fragment;
    }

    const pattern = new RegExp(`(${escapeRegExp(term)})`, 'ig');
    text.split(pattern).forEach((part) => {
      if (!part) return;
      if (part.toLowerCase() === term.toLowerCase()) {
        const mark = document.createElement('mark');
        mark.textContent = part;
        fragment.append(mark);
      } else {
        fragment.append(document.createTextNode(part));
      }
    });

    return fragment;
  };

  const formatPrice = (amount, currency) => {
    const value = Number.parseFloat(amount);
    if (Number.isNaN(value) || !currency) return amount;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(value);
    } catch (error) {
      return amount;
    }
  };

  const strings = () => window.wowthingsStrings || {};

  const buildGroup = (labelText) => {
    const group = document.createElement('div');
    group.className = 'predictive-search__group';

    if (labelText) {
      const label = document.createElement('p');
      label.className = 'predictive-search__group-label';
      label.textContent = labelText;
      group.append(label);
    }

    return group;
  };

  const buildOption = (id, href) => {
    const option = document.createElement('a');
    option.id = id;
    option.href = href;
    option.className = 'predictive-search__option';
    option.setAttribute('role', 'option');
    return option;
  };

  class PredictiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('[data-predictive-search-input]');
      this.results = form.querySelector('[data-predictive-search-results]');
      this.searchUrl = form.dataset.predictiveSearchUrl;
      this.currency = form.dataset.currency;
      this.controller = null;
      this.timer = null;
      this.activeIndex = -1;

      if (!this.input || !this.results || !this.searchUrl) return;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('click', (event) => {
        if (!this.form.contains(event.target)) this.close();
      });
      this.form.closest('dialog')?.addEventListener('close', () => this.close());
    }

    onInput() {
      window.clearTimeout(this.timer);
      const term = this.input.value.trim();

      if (term.length < MIN_QUERY_LENGTH) {
        this.close();
        return;
      }

      this.timer = window.setTimeout(() => this.search(term), DEBOUNCE_MS);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1);
        this.focusOption(options);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.focusOption(options);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        options[this.activeIndex]?.click();
      }
    }

    focusOption(options) {
      options.forEach((option, index) => {
        option.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false');
      });
      const active = options[this.activeIndex];
      if (active) this.input.setAttribute('aria-activedescendant', active.id);
    }

    close() {
      this.results.hidden = true;
      this.results.textContent = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }

    async search(term) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });

      let payload;
      try {
        const response = await fetch(`${this.searchUrl}.json?${params.toString()}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) return;
        payload = await response.json();
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
        return;
      }

      this.render(payload?.resources?.results ?? {}, term);
    }

    render(results, term) {
      this.results.textContent = '';
      this.activeIndex = -1;

      const queries = results.queries ?? [];
      const collections = results.collections ?? [];
      const products = results.products ?? [];
      const hasResults = queries.length || collections.length || products.length;

      if (!hasResults) {
        const empty = document.createElement('p');
        empty.className = 'predictive-search__empty';
        empty.textContent = strings().predictiveNoResults ?? '';
        this.results.append(empty);
      } else {
        if (queries.length) {
          const group = buildGroup(strings().suggestions);
          queries.forEach((query, index) => {
            const option = buildOption(`PredictiveSuggestion-${index}`, query.url);
            option.append(highlightText(query.text, term));
            group.append(option);
          });
          this.results.append(group);
        }

        if (collections.length) {
          const group = buildGroup(strings().collections);
          collections.forEach((collection, index) => {
            const option = buildOption(`PredictiveCollection-${index}`, collection.url);
            const title = document.createElement('span');
            title.className = 'predictive-search__option-title';
            title.append(highlightText(collection.title, term));
            option.append(title);
            group.append(option);
          });
          this.results.append(group);
        }

        if (products.length) {
          const group = buildGroup(strings().products);
          products.forEach((product, index) => {
            const option = buildOption(`PredictiveProduct-${index}`, product.url);

            if (product.image) {
              const img = document.createElement('img');
              img.src = product.image;
              img.alt = '';
              img.loading = 'lazy';
              option.append(img);
            }

            const details = document.createElement('span');
            const title = document.createElement('span');
            title.className = 'predictive-search__option-title';
            title.append(highlightText(product.title, term));
            const price = document.createElement('span');
            price.className = 'predictive-search__option-price';
            price.textContent = formatPrice(product.price, this.currency);
            details.append(title, price);
            option.append(details);

            group.append(option);
          });
          this.results.append(group);
        }
      }

      const viewAll = document.createElement('a');
      viewAll.href = `${this.form.action}?q=${encodeURIComponent(term)}`;
      viewAll.className = 'predictive-search__view-all';
      viewAll.textContent = (strings().viewAllResults ?? '{{ terms }}').replace('{{ terms }}', term);
      this.results.append(viewAll);

      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
  }

  document.querySelectorAll('[data-predictive-search]').forEach((form) => new PredictiveSearch(form));
})();
